import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Funnel } from "./FunnelAdmin";

const PERIODS = [[7, "7 jours"], [30, "30 jours"], [90, "90 jours"], [365, "1 an"]];
const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
const fmtLong = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

function Stat({ id, label, v }) {
  return (
    <div data-testid={`admin-visits-${id}`} className="rounded-sm border border-[var(--line)] p-5">
      <p className="text-xs uppercase tracking-[0.16em] text-[var(--gold-soft)]">{label}</p>
      <p className="mt-2 font-display text-3xl">{v?.visitors ?? 0} <span className="text-base text-[var(--parchment)]/80">visiteurs</span></p>
      <p className="text-sm text-[var(--parchment)]/80">{v?.views ?? 0} pages vues</p>
    </div>
  );
}

function Chart({ series }) {
  const max = Math.max(1, ...series.map((s) => s.visitors));
  return (
    <div data-testid="admin-visits-chart" className="mt-6 rounded-sm border border-[var(--line)] p-5">
      <p className="text-sm text-[var(--parchment)]/85">Visiteurs par jour</p>
      <div className="mt-4 flex h-40 items-end gap-[2px]">
        {series.map((s) => (
          <div key={s.day} title={`${fmtDay(s.day)} : ${s.visitors} visiteurs, ${s.views} pages vues`} className="flex-1 rounded-t-sm bg-[var(--gold)]/80 transition-colors hover:bg-[var(--gold-soft)]"
            style={{ height: `${Math.max(2, (s.visitors / max) * 100)}%` }} />
        ))}
      </div>
      <div className="mt-2 flex justify-between text-xs text-[var(--parchment)]/70"><span>{series[0] ? fmtDay(series[0].day) : ""}</span><span>{series.length ? fmtDay(series[series.length - 1].day) : ""}</span></div>
    </div>
  );
}

function DailyTable({ series }) {
  const rows = [...series].reverse();
  return (
    <div data-testid="admin-visits-daily" className="mt-6 rounded-sm border border-[var(--line)] p-5">
      <p className="text-sm text-[var(--gold-soft)]">Visites jour par jour et provenance</p>
      <p className="mt-1 text-xs text-[var(--parchment)]/75">Un rapport vous est envoyé par email uniquement les jours où il y a eu des ventes.</p>
      <div className="mt-3 max-h-[480px] overflow-auto">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 bg-[var(--ink)] text-xs uppercase tracking-[0.12em] text-[var(--parchment)]/70"><tr><th className="py-2">Jour</th><th className="py-2 text-right">Visiteurs</th><th className="py-2 text-right">Pages vues</th><th className="hidden py-2 pl-6 md:table-cell">Provenance des visites</th><th className="hidden py-2 text-right lg:table-cell">Commandes</th><th className="hidden py-2 text-right lg:table-cell">Chiffre d'affaires</th><th className="hidden py-2 text-right lg:table-cell">Abonnements</th></tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.day} data-testid={`admin-visits-day-${s.day}`} className="border-t border-[var(--line)] align-top">
                <td className="py-2 pr-2"><span className="block first-letter:uppercase">{fmtLong(s.day)}</span><span className="mt-1.5 block md:hidden"><Chips s={s} field="visit_sources" where="visits-mobile" /></span></td>
                <td className="py-2 text-right font-semibold">{s.visitors}</td>
                <td className="py-2 text-right text-[var(--parchment)]/80">{s.views}</td>
                <td className="hidden py-2 pl-6 md:table-cell"><Chips s={s} field="visit_sources" where="visits-desktop" /></td>
                <td className="hidden py-2 text-right lg:table-cell">{s.orders ?? 0}</td>
                <td className="hidden py-2 text-right lg:table-cell">{eurFr(s.revenue)}</td>
                <td className="hidden py-2 text-right lg:table-cell">{s.subscriptions ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const eurFr = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

function Chips({ s, where, field = "sources" }) {
  return (
    <span className="flex flex-wrap gap-1.5" data-testid={`admin-sales-sources-${where}-${s.day}`}>
      {(Array.isArray(s[field]) ? s[field] : []).map((x) => (
        <span key={x.source} className="rounded-full border border-[var(--line)] px-2.5 py-0.5 text-xs">{x.source}{x.count > 1 ? ` × ${x.count}` : ""}</span>
      ))}
    </span>
  );
}

function SalesByDay({ series }) {
  const rows = [...series].reverse().filter((s) => (s.orders ?? 0) + (s.subscriptions ?? 0) > 0);
  const tot = rows.reduce((a, s) => ({ o: a.o + (s.orders ?? 0), r: a.r + (s.revenue || 0), a: a.a + (s.subscriptions ?? 0) }), { o: 0, r: 0, a: 0 });
  return (
    <div data-testid="admin-sales-by-day" className="mt-6 rounded-sm border border-[var(--gold)]/50 p-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-[var(--gold-soft)]">Ventes jour par jour et provenance</p>
        <p data-testid="admin-sales-by-day-total" className="text-sm text-[var(--parchment)]/85">{tot.o} commande{tot.o > 1 ? "s" : ""} · {eurFr(tot.r)} · {tot.a} abonnement{tot.a > 1 ? "s" : ""}</p>
      </div>
      {rows.length ? (
        <div className="mt-3 max-h-[480px] overflow-auto">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-[var(--ink)] text-xs uppercase tracking-[0.12em] text-[var(--parchment)]/70"><tr><th className="py-2">Jour</th><th className="py-2 text-right">Commandes</th><th className="py-2 text-right">Chiffre d'affaires</th><th className="hidden py-2 text-right md:table-cell">Abonnements</th><th className="hidden py-2 pl-6 md:table-cell">Provenance</th></tr></thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.day} data-testid={`admin-sales-day-${s.day}`} className="border-t border-[var(--line)] align-top">
                  <td className="py-2 pr-2"><span className="block first-letter:uppercase">{fmtLong(s.day)}</span><span className="mt-1.5 block md:hidden"><Chips s={s} where="mobile" /></span>{s.subscriptions ? <span className="mt-1 block text-xs text-[var(--parchment)]/80 md:hidden">{s.subscriptions} abonnement{s.subscriptions > 1 ? "s" : ""}</span> : null}</td>
                  <td className="py-2 text-right font-semibold">{s.orders ?? 0}</td>
                  <td className="py-2 text-right">{eurFr(s.revenue)}</td>
                  <td className="hidden py-2 text-right md:table-cell">{s.subscriptions ?? 0}</td>
                  <td className="hidden py-2 pl-6 md:table-cell"><Chips s={s} where="desktop" /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-[var(--parchment)]/70">Provenance : le site d'où la cliente est arrivée (Google, Instagram, Facebook, WhatsApp…), « Accès direct » si elle a tapé l'adresse, « Non suivie » si l'information n'a pas pu être enregistrée.</p>
        </div>
      ) : <p className="mt-3 text-sm text-[var(--parchment)]/70">Aucune vente sur la période.</p>}
    </div>
  );
}

function Top({ id, title, rows, unit }) {
  return (
    <div data-testid={`admin-visits-top-${id}`} className="rounded-sm border border-[var(--line)] p-5">
      <p className="text-sm text-[var(--gold-soft)]">{title}</p>
      {rows.length ? (
        <ul className="mt-3 space-y-2 text-sm">
          {rows.map((r) => <li key={r.label} className="flex justify-between gap-4"><span className="truncate">{r.label}</span><span className="shrink-0 text-[var(--parchment)]/80">{r.count} {unit}</span></li>)}
        </ul>
      ) : <p className="mt-3 text-sm text-[var(--parchment)]/70">Pas encore de données.</p>}
    </div>
  );
}

function Sales({ s }) {
  const rows = Array.isArray(s?.by_source) ? s.by_source : [];
  const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  return (
    <div data-testid="admin-visits-sales" className="mt-6 rounded-sm border border-[var(--line)] p-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <p className="text-sm text-[var(--gold-soft)]">Des visites aux ventes</p>
        <p data-testid="admin-visits-conversion" className="text-sm text-[var(--parchment)]/85">
          {s?.orders ?? 0} commande{(s?.orders ?? 0) > 1 ? "s" : ""} payée{(s?.orders ?? 0) > 1 ? "s" : ""} · {eur(s?.revenue)} · taux de conversion : <b className="text-[var(--parchment)]">{s?.conversion == null ? "—" : `${String(s.conversion).replace(".", ",")} %`}</b>
        </p>
      </div>
      {rows.length ? (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[520px] text-left text-sm">
            <thead className="text-xs uppercase tracking-[0.12em] text-[var(--parchment)]/70"><tr><th className="py-2">Source</th><th className="py-2">Visiteurs</th><th className="py-2">Commandes</th><th className="py-2">Chiffre d'affaires</th><th className="py-2">Conversion</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.source} data-testid={`admin-visits-sales-row-${r.source}`} className="border-t border-[var(--line)]">
                  <td className="py-2">{r.source}</td>
                  <td className="py-2">{r.source === "Non suivie" ? "—" : r.visitors}</td>
                  <td className="py-2">{r.orders}</td>
                  <td className="py-2">{eur(r.revenue)}</td>
                  <td className="py-2">{r.visitors && r.source !== "Non suivie" ? `${(Math.round((10000 * r.orders) / r.visitors) / 100).toString().replace(".", ",")} %` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-[var(--parchment)]/70">« Non suivie » : commandes passées avant la mise en place du compteur. La source est celle par laquelle la cliente est arrivée sur le site.</p>
        </div>
      ) : <p className="mt-3 text-sm text-[var(--parchment)]/70">Pas encore de données.</p>}
    </div>
  );
}

function AmbSales({ rows }) {
  const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  const tot = rows.reduce((a, r) => ({ orders: a.orders + r.orders, revenue: a.revenue + r.revenue }), { orders: 0, revenue: 0 });
  return (
    <div data-testid="admin-visits-ambassadors" className="mt-6 rounded-sm border border-[var(--line)] p-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <p className="text-sm text-[var(--gold-soft)]">Ventes par ambassadeur (codes -20 %)</p>
        <p data-testid="admin-visits-amb-total" className="text-sm text-[var(--parchment)]/85">{tot.orders} commande{tot.orders > 1 ? "s" : ""} · {eur(tot.revenue)}</p>
      </div>
      {rows.length ? (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[520px] text-left text-sm">
            <thead className="text-xs uppercase tracking-[0.12em] text-[var(--parchment)]/70"><tr><th className="py-2">Ambassadeur</th><th className="py-2">Code</th><th className="py-2">Commandes</th><th className="py-2">Chiffre d'affaires</th><th className="py-2">Remises accordées</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.code} data-testid={`admin-visits-amb-row-${r.code}`} className="border-t border-[var(--line)]">
                  <td className="py-2">{r.name}</td><td className="py-2 text-[var(--gold-soft)]">{r.code}</td><td className="py-2">{r.orders}</td><td className="py-2">{eur(r.revenue)}</td><td className="py-2">{eur(r.discount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-[var(--parchment)]/70">Commandes payées avec le code de l'ambassadeur sur la période. Le chiffre d'affaires est le montant payé par la cliente (remise déduite, livraison comprise).</p>
        </div>
      ) : <p className="mt-3 text-sm text-[var(--parchment)]/70">Aucun ambassadeur avec un code actif pour l'instant.</p>}
    </div>
  );
}

export function VisitsToday({ onOpen }) {
  const { data } = useQuery({ queryKey: ["admin-visits", 7], queryFn: () => api.get("/admin/visits?days=7").then((r) => r.data), refetchInterval: 60000 });
  const series = Array.isArray(data?.series) ? data.series : [];
  const y = series.length > 1 ? series[series.length - 2] : null;
  return (
    <button type="button" onClick={onOpen} data-testid="dashboard-visits-today"
      className="mt-4 flex w-full flex-wrap items-center gap-x-8 gap-y-2 rounded-sm border border-[var(--gold)]/50 bg-[rgba(217,180,106,0.06)] p-5 text-left transition-colors hover:border-[var(--gold)]">
      <span className="text-xs uppercase tracking-[0.16em] text-[var(--gold-soft)]">Visites du site</span>
      <span data-testid="dashboard-visits-today-count"><b className="font-display text-3xl">{data?.today?.visitors ?? 0}</b> <span className="text-sm text-[var(--parchment)]/85">visiteurs aujourd'hui ({data?.today?.views ?? 0} pages vues)</span></span>
      <span data-testid="dashboard-visits-yesterday-count" className="text-sm text-[var(--parchment)]/85">Hier : <b className="text-[var(--parchment)]">{y?.visitors ?? 0}</b> visiteurs</span>
      <span className="text-sm text-[var(--parchment)]/85">7 jours : <b className="text-[var(--parchment)]">{data?.week?.visitors ?? 0}</b></span>
      <span className="ml-auto text-xs text-[var(--gold-soft)] underline underline-offset-4">Voir le détail jour par jour</span>
    </button>
  );
}

export function VisitsAdmin() {
  const [days, setDays] = useState(30);
  const { data } = useQuery({ queryKey: ["admin-visits", days], queryFn: () => api.get(`/admin/visits?days=${days}`).then((r) => r.data), refetchInterval: 60000 });
  const series = Array.isArray(data?.series) ? data.series : [];
  return (
    <div data-testid="admin-visits">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl">Visites du site</h2>
          <p className="mt-1 text-sm text-[var(--parchment)]/85">Comptage anonyme sans cookie, robots et administration exclus{data?.since ? ` · données depuis le ${fmtDay(data.since)}` : ""}</p>
        </div>
        <div className="flex gap-2">
          {PERIODS.map(([n, l]) => (
            <button key={n} onClick={() => setDays(n)} data-testid={`admin-visits-period-${n}`}
              className={`h-9 rounded-full border px-4 text-xs font-semibold ${days === n ? "border-[var(--gold)] bg-[var(--gold)] text-[var(--ink)]" : "border-[var(--line)] text-[var(--parchment)]"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat id="today" label="Aujourd'hui" v={data?.today} />
        <Stat id="yesterday" label="Hier" v={series.length > 1 ? series[series.length - 2] : null} />
        <Stat id="week" label="7 derniers jours" v={data?.week} />
        <Stat id="period" label={`${days} derniers jours`} v={data?.period} />
      </div>
      <Funnel p={data?.period} days={days} series={series} />
      <Chart series={series} />
      <SalesByDay series={series} />
      <DailyTable series={series} />
      <Sales s={data?.sales} />
      <AmbSales rows={Array.isArray(data?.sales?.by_ambassador) ? data.sales.by_ambassador : []} />
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <Top id="pages" title="Pages les plus vues" rows={Array.isArray(data?.top_pages) ? data.top_pages : []} unit="vues" />
        <Top id="sources" title="D'où viennent les visiteurs" rows={Array.isArray(data?.top_sources) ? data.top_sources : []} unit="visiteurs" />
        <Top id="countries" title="Pays des visiteurs (estimé d'après le fuseau horaire)" rows={Array.isArray(data?.top_countries) ? data.top_countries : []} unit="visiteurs" />
      </div>
    </div>
  );
}
